import { ApolloClient, InMemoryCache } from "@apollo/client";
import { RefreshTokenDocument } from "../generated";

// Utility function to decode JWT and get expiry
const getTokenExpiry = (token: string): number | null => {
  try {
    // Decode JWT payload (second part)
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map((c) => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join("")
    );

    const decoded = JSON.parse(jsonPayload);
    return decoded.exp || null; // Unix timestamp
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

// Get TTL in seconds
export const getTokenTTL = (token: string): number | null => {
  const expiry = getTokenExpiry(token);
  if (!expiry) return null;

  const currentTime = Math.floor(Date.now() / 1000);
  const ttl = expiry - currentTime;

  return ttl > 0 ? ttl : 0; // Return 0 if already expired
};

// Check if token needs refresh (expired or expires within 5 minutes)
const shouldRefreshToken = (token: string): boolean => {
  const ttl = getTokenTTL(token);
  return ttl !== null && ttl <= 300; // 5 minutes threshold
};

// Function to refresh token using GraphQL mutation (same as AppApolloProvider)
export const refreshTokenIfNeeded = async (
  token: string,
  apolloClient?: ApolloClient<any>
): Promise<string> => {
  if (!shouldRefreshToken(token)) {
    return token; // Token is still valid
  }

  if (!apolloClient) {
    console.warn(
      "⚠️ Apollo client not available for token refresh, returning original token"
    );
    return token;
  }

  try {
    console.log("🔄 Token expired or expiring soon, refreshing...");

    const refreshToken = localStorage.getItem("refreshToken") || "";

    if (!refreshToken) {
      console.error("❌ No refresh token available");
      return token;
    }

    const refreshResolverResponse = await apolloClient.mutate({
      mutation: RefreshTokenDocument,
      variables: {
        input: {
          refreshToken,
        },
      },
    });

    const accessToken = refreshResolverResponse.data?.refreshToken.idToken;
    const accessNewToken =
      refreshResolverResponse.data?.refreshToken.accessToken;

    if (accessToken) {
      localStorage.setItem("token", accessToken);
    }
    if (accessNewToken) {
      localStorage.setItem("accessToken", accessNewToken);
    }

    if (accessToken && accessToken !== token) {
      console.log("✅ Token refreshed successfully");
      return accessToken;
    } else {
      console.warn("⚠️ Token refresh may not have occurred");
      return token;
    }
  } catch (error) {
    console.error("❌ Failed to refresh token:", error);
    // Return original token, let the subsequent API call handle the auth error
    return token;
  }
};

// Utility function to get token info for debugging
export const getTokenInfo = (token: string) => {
  const ttlSeconds = getTokenTTL(token);

  if (ttlSeconds !== null) {
    const ttlMinutes = Math.floor(ttlSeconds / 60);
    const ttlHours = Math.floor(ttlMinutes / 60);
    const remainingMinutes = ttlMinutes % 60;

    return {
      ttlSeconds,
      ttlMinutes,
      ttlHours,
      remainingMinutes,
      isExpired: ttlSeconds <= 0,
      isExpiringSoon: ttlSeconds <= 300, // 5 minutes
      humanReadable: `${ttlHours}h ${remainingMinutes}m`,
    };
  }

  return null;
};
